/**
 * ConsentActionsDialog — Admin-Aktionen für Einverständnis-Erklärungen:
 * - mode="sign":    Kunde unterschreibt vor Ort auf dem iPad/Tablet (PDF geht per E-Mail raus)
 * - mode="request": Unterschrift-Link per WhatsApp/E-Mail an den Kunden senden
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { MessageCircle, Mail, PenLine, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ConsentSignSection from '../../portal/ConsentSignSection';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ConsentActionsDialog = ({ open, onClose, mode, formId, formTitle, customer, onSigned }) => {
  const [form, setForm] = useState(null);
  const [payload, setPayload] = useState(null);
  const [channels, setChannels] = useState({ whatsapp: true, email: true });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || mode !== 'sign' || !formId) return;
    setForm(null);
    setPayload(null);
    axios.get(`${API}/public/consent-forms/${formId}`)
      .then(res => setForm(res.data))
      .catch(() => toast.error('Formular konnte nicht geladen werden'));
  }, [open, mode, formId]);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const submitSign = async () => {
    if (!payload) return;
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/customers/${customer.id}/consents/sign-onsite`,
        payload,
        { headers: authHeaders() }
      );
      toast.success(res.data.pdf_emailed
        ? 'Unterschrift gespeichert — PDF wurde per E-Mail an den Kunden gesendet ✅'
        : 'Unterschrift gespeichert ✅');
      onSigned?.(formId);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const submitRequest = async () => {
    const selected = Object.keys(channels).filter(k => channels[k]);
    if (selected.length === 0) { toast.error('Mindestens einen Kanal wählen'); return; }
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/customers/${customer.id}/consents/request`,
        { form_id: formId, channels: selected },
        { headers: authHeaders() }
      );
      const s = res.data.sent || {};
      const ok = [s.whatsapp && 'WhatsApp', s.email && 'E-Mail'].filter(Boolean).join(' + ');
      toast.success(`Unterschrift-Link gesendet via ${ok} ✅`);
      onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Versand fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className={mode === 'sign' ? 'max-w-2xl max-h-[92vh] overflow-y-auto' : 'max-w-md'}
        aria-describedby={undefined}
        data-testid="consent-actions-dialog"
      >
        <DialogHeader>
          <DialogTitle>
            {mode === 'sign' ? `✍️ Vor Ort unterschreiben — ${formTitle}` : `📤 Unterschrift-Link senden — ${formTitle}`}
          </DialogTitle>
        </DialogHeader>

        {mode === 'sign' ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Gerät dem Kunden übergeben: <strong>{customer?.first_name} {customer?.last_name}</strong> liest die Erklärung und unterschreibt unten.
            </p>
            {form ? (
              <div className="bg-zinc-900 rounded-xl p-4">
                <h3 className="text-white font-semibold mb-3">{form.title}</h3>
                <ConsentSignSection form={form} onChange={setPayload} />
              </div>
            ) : (
              <div className="flex items-center justify-center py-10 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
            )}
            <Button
              className="w-full"
              disabled={!payload || busy}
              onClick={submitSign}
              data-testid="consent-onsite-submit"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PenLine className="w-4 h-4 mr-2" />}
              Unterschrift speichern & PDF per E-Mail senden
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Der Kunde erhält einen persönlichen Link und kann die Erklärung direkt am Handy unterschreiben.
            </p>
            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <Checkbox
                checked={channels.whatsapp}
                onCheckedChange={(v) => setChannels(c => ({ ...c, whatsapp: !!v }))}
                data-testid="consent-request-whatsapp"
              />
              <MessageCircle className="w-4 h-4 text-green-600" />
              <span className="text-sm">WhatsApp {customer?.phone ? `(${customer.phone})` : '(keine Nummer hinterlegt)'}</span>
            </label>
            <label className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50">
              <Checkbox
                checked={channels.email}
                onCheckedChange={(v) => setChannels(c => ({ ...c, email: !!v }))}
                data-testid="consent-request-email"
              />
              <Mail className="w-4 h-4 text-blue-600" />
              <span className="text-sm">E-Mail {customer?.email ? `(${customer.email})` : '(keine E-Mail hinterlegt)'}</span>
            </label>
            <Button className="w-full" disabled={busy} onClick={submitRequest} data-testid="consent-request-submit">
              {busy ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Link jetzt senden
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConsentActionsDialog;
